import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IIdName } from "api/entities/id-name";
import {
  AccessCredentialsRouteAction,
  DefaultRouteParam,
} from "constants/routes";
import { formUtils, IFormField, useField } from "utils/form-utils";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { FormPanel } from "components/shared-ui/form/form-panel/form-panel";
import { AccessCodesGeneratedDialog } from "components/access-credentials/dialogs/access-codes-generated-dialog/access-codes-generated-dialog";
import { CredentialsUserSection } from "components/access-credentials/sections/credentials-bucket-section/credentials-user-section";
import styles from "./credentials-panel.module.scss";
import { UserListFilter } from "components/access-credentials/filters/credential-panel-filters/credential-user-section-filters";
import { IIAMCredential } from "api/entities/iam-credentials";
import { IIAMUserRequest } from "api/entities/iam-users";

export interface IApplicationCredentialsPanelProps {
  application?: IIdName<string>;

  onClose: () => void;
  onCreateApplicationCredentials: (
    createRequest: IIAMUserRequest
  ) => Promise<IIAMCredential>;
  onRefreshApplicationCredentials?: () => void;
}

export interface IApplicationCredentialsForm {
  userName: IFormField<string>;
}

export interface IApplicationCredentialsFormValue {
  userName: string;
}

const formDefaultValue: IApplicationCredentialsFormValue = {
  userName: "",
};

export function CredentialsPanel(props: IApplicationCredentialsPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const open = idOrAction === AccessCredentialsRouteAction.AddCredentials;

  const [savedApplicationCredentials, setSavedApplicationCredentials] =
    useState<IIAMCredential>(null);
  const [
    isAccessCodesGeneratedDialogOpen,
    setIsAccessCodesGeneratedDialogOpen,
  ] = useState<boolean>(false);

  const form: IApplicationCredentialsForm = {
    userName: useField<string>(formDefaultValue.userName),
  };

  const formError = formUtils.getFormError(form);

  function setUserFormValue(userName: string) {
    const formValue: Partial<IApplicationCredentialsFormValue> = {
      userName: userName,
    };
    formUtils.setFormValue(form, formValue);
  }

  function saveIAMCredential() {
    const formValue =
      formUtils.getFormValue<IApplicationCredentialsFormValue>(form);

    props
      .onCreateApplicationCredentials({ UserName: formValue.userName })
      .then(setSavedApplicationCredentials)
      .then(() => props.onClose())
      .then(() => setIsAccessCodesGeneratedDialogOpen(true));
  }

  function closeAccessCodesGeneratedDialog() {
    setIsAccessCodesGeneratedDialogOpen(false);

    if (!!props.onRefreshApplicationCredentials) {
      props.onRefreshApplicationCredentials();
    }
  }

  useEffect(() => {
    if (!!open) {
      formUtils.setFormValue(form, formDefaultValue);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      <DetailsPanel
        isLoading={false}
        open={open}
        onCloseClick={props.onClose}
        title="Add Access Credential"
        className={styles.panel}
      >
        {open && (
          <FormPanel
            okButtonText="Save"
            okButtonClick={saveIAMCredential}
            okButtonDisabled={!form.userName.value && !!formError}
            errorMessage={formError}
          >
            <div className={styles.description}>
              Access Credentials are essential for enabling secure interactions
              with your Bucket. These access keys authenticate requests and
              ensure that your applications can safely access the stored data.
              Proper management of these credentials is crucial for maintaining
              the security of your data environment.
            </div>

            <div className={styles["access-credentials-fields"]}>
              <CredentialsUserSection
                selectedUserName={form.userName.value}
                onSelectUserName={setUserFormValue}
                visibleFilters={[UserListFilter.Search]}
              />
            </div>
          </FormPanel>
        )}
      </DetailsPanel>
      <AccessCodesGeneratedDialog
        open={isAccessCodesGeneratedDialogOpen}
        applicationCredentials={savedApplicationCredentials}
        onClose={() => closeAccessCodesGeneratedDialog()}
      />
    </>
  );
}
